/**
 * `expenses` — the cash ledger `.docs/00-index.md` §6 adds in session 17: "Not in
 * `ARCHITECTURE.md`. Added as a small `expenses` module: append-only `expense`
 * ledger (kind: contractor, subscription, rail_fee, draw, other) and
 * `cash_position(period, opening_amount)`. Read-only cash view; not accounting."
 *
 * `mockups/studio/money.html` §"Money out" is what every field name is chosen to
 * print without translation: `payee`, `purpose`, `basis` (its "3 h @ $45"), `rail`.
 */
import { z } from 'zod';
import { DateOnlySchema, MoneySchema, PeriodSchema } from './primitives.js';
/** `.docs/17-studio-read-models.md` §A. Closed, and `expense.kind` is `inCheck` over it. */
export const EXPENSE_KINDS = ['contractor', 'subscription', 'rail_fee', 'draw', 'other'];
export const ExpenseKindSchema = z
    .enum(EXPENSE_KINDS)
    .describe('What the money was for: contractor (a freelancer, often by the hour), subscription ' +
    '(tooling), rail_fee (what Stripe or Wise kept), draw (the owner’s), or other.');
/**
 * How the money left the account. Not a lifecycle with transitions — `report.status`
 * (session 16) is the precedent for `text` + `inCheck` over a `stateEnum`: three
 * values and nothing that moves between them on its own. `sent` is a bank transfer
 * in flight, `paid` is confirmed, `deducted` is a rail fee taken straight off a
 * settlement with no separate confirmation to wait for.
 */
export const EXPENSE_STATUSES = ['paid', 'sent', 'deducted'];
export const ExpenseStatusSchema = z
    .enum(EXPENSE_STATUSES)
    .describe('paid (confirmed), sent (a transfer in flight), or deducted (taken automatically).');
export const RecordExpenseSchema = z.strictObject({
    paid_on: DateOnlySchema.describe('The day the money left the account.'),
    payee: z.string().trim().min(1).max(200).describe('Who was paid — a name, not an address.'),
    purpose: z.string().trim().min(1).max(300).describe('What it was for, in a phrase.'),
    basis: z
        .string()
        .trim()
        .max(200)
        .describe('How the amount was arrived at, printed verbatim — "3 h @ $45", "Monthly seat, 4 users". ' +
        'Empty for a flat amount that needs no explaining, like the draw.'),
    kind: ExpenseKindSchema,
    amount: MoneySchema.describe('What left the account. USD only — this ledger prices in USD.'),
    rail: z
        .string()
        .trim()
        .max(60)
        .nullable()
        .describe('How it left — wise, stripe, ach, card, cash — or null when it does not apply.'),
    status: ExpenseStatusSchema,
    hours: z
        .number()
        .positive()
        .max(1000)
        .nullable()
        .describe('For a contractor paid by the hour: how many. Null otherwise.'),
    rate: MoneySchema.nullable().describe('For a contractor paid by the hour: the rate. Null otherwise.'),
    period: PeriodSchema.describe('The month this counts toward in the studio’s money-out figures.'),
    ref: z
        .string()
        .trim()
        .max(200)
        .nullable()
        .describe('The gateway’s own id for an automatically recorded rail fee — a Stripe balance ' +
        'transaction, a Wise transfer — and the idempotency key expenses.recordRailFee keys ' +
        'on. Null for anything staff typed in by hand.'),
});
export const UpdateExpenseSchema = RecordExpenseSchema.partial();
export const ExpenseSchema = RecordExpenseSchema.extend({
    id: z.uuid().describe('What expenses_update$ and expenses_list address it by.'),
    created_at: z.string().describe('When the row was written, RFC 3339.'),
}).describe('One line of money out. Append-only in spirit — expenses_update$ corrects a row.');
export const SetOpeningBalanceSchema = z.strictObject({
    period: PeriodSchema.describe('The month the opening balance is for.'),
    opening_amount: MoneySchema.describe('What the account held on the first of that month.'),
});
export const CashPositionSchema = z.strictObject({
    period: PeriodSchema,
    opening_amount: MoneySchema,
});
//# sourceMappingURL=expenses.js.map